import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { makeErrorToast } from 'shared/components/Toaster';
import { useCopilot } from 'shared/context/CopilotContext';
import { partitionComposerFiles } from 'shared/copilot/utils/imageAttachment';
import { nameGenericPastedImages } from 'shared/copilot/utils/pastedImageName';
import { useTranslation } from 'shared/translations';
export interface UseCopilotComposerFileIntakeResult {
  /** Routes files to the image and CSV channels as they are, e.g. picked with the attach button. */
  addFiles: (files: readonly File[]) => void;
  addPastedFiles: (files: readonly File[]) => void;
}

/**
 * The one way files enter the Copilot composer, whether picked or pasted.
 *
 * Each file is routed by kind to its own channel — up to ten images as message
 * content parts, and a single CSV (a new one replaces the current attachment).
 * The user should not have to know that those are two different mechanisms,
 * nor get different rules depending on how the file arrived.
 */
export const useCopilotComposerFileIntake = () => {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "copilot.composer"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("common", t0);
  const {
    attachCsvFile,
    attachImageFiles,
    imageAttachments,
    isCsvUploading
  } = useCopilot();
  let t1;
  if ($[1] !== attachCsvFile || $[2] !== attachImageFiles || $[3] !== t) {
    t1 = files => {
      if (files.length === 0) {
        return;
      }
      const {
        csv,
        images,
        unsupported
      } = partitionComposerFiles(files);
      if (images.length > 0 || unsupported.length > 0) {
        attachImageFiles([...images, ...unsupported]);
      }
      if (csv.length > 1) {
        makeErrorToast(t("csvAttachment.errors.multipleFilesSelected"));
      }
      const lastCsv = csv.at(-1);
      if (lastCsv != null) {
        attachCsvFile(lastCsv);
      }
    };
    $[1] = attachCsvFile;
    $[2] = attachImageFiles;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const addFiles = t1;
  let t2;
  if ($[5] !== addFiles || $[6] !== imageAttachments || $[7] !== isCsvUploading || $[8] !== t) {
    t2 = files_0 => {
      if (files_0.length === 0) {
        return;
      }
      if (isCsvUploading) {
        makeErrorToast(t("pasteBlockedWhileCsvUploading"));
        return;
      }
      const attachedNames = imageAttachments.map(_temp);
      addFiles(nameGenericPastedImages(files_0, timestamp => t("pastedImageName", {
        timestamp
      }), attachedNames));
    };
    $[5] = addFiles;
    $[6] = imageAttachments;
    $[7] = isCsvUploading;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const addPastedFiles = t2;
  let t3;
  if ($[10] !== addFiles || $[11] !== addPastedFiles) {
    t3 = {
      addFiles,
      addPastedFiles
    };
    $[10] = addFiles;
    $[11] = addPastedFiles;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
function _temp(attachment) {
  return attachment.filename;
}